<template>
  <Layout>
    <PageHeader :title="title" />

    <div class="modern-wrapper">
      <!-- Top Control Bar -->
      <div class="card shadow-sm border-0 mb-4 rounded-xl">
        <div class="card-body p-3 p-md-4">
          <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3"
          >
            <!-- Search Filter -->
            <div class="search-box position-relative flex-grow-1" style="max-width: 400px">
              <i class="mdi mdi-magnify search-icon text-muted"></i>
              <b-form-input
                v-model="filters.search"
                placeholder="Search by code or reason name..."
                class="form-control-rounded bg-light border-0 ps-5"
                @input="onSearchInput"
              />
            </div>

            <!-- Action Button -->
            <div class="d-flex align-items-center gap-2">
              <b-button
                v-if="filters.search"
                variant="link"
                class="text-muted text-decoration-none px-2"
                @click="resetFilters"
              >
                Reset
              </b-button>
              <b-button
                variant="primary"
                class="rounded-pill shadow-sm px-4"
                @click="openCreateModal"
              >
                <i class="mdi mdi-plus me-1"></i> Add Reason
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Contextual Selection Toolbar (Bulk Delete Action) -->
      <b-collapse :visible="selectedIds.length > 0" class="mb-4">
        <div
          class="selection-toolbar d-flex align-items-center justify-content-between p-3 rounded-xl shadow-sm"
        >
          <div class="d-flex align-items-center gap-3 ms-2">
            <span class="fw-medium text-primary"> {{ selectedIds.length }} item(s) selected </span>
          </div>
          <b-button
            variant="danger"
            class="rounded-pill px-3 shadow-sm"
            @click="openBulkDeleteModal"
          >
            <i class="mdi mdi-trash-can-outline me-1"></i> Delete Selected
          </b-button>
        </div>
      </b-collapse>

      <!-- Main Data Table Container -->
      <div class="card shadow-sm border-0 rounded-xl mb-4 overflow-hidden">
        <b-table
          :items="items"
          :fields="fields"
          :busy="isLoading"
          responsive="sm"
          hover
          borderless
          align-v="center"
          class="align-middle mb-0 custom-table"
          show-empty
        >
          <!-- Custom Header Checkbox (Select All) -->
          <template #head(selected)>
            <b-form-checkbox
              :checked="isAllSelected"
              :indeterminate="isSomeSelected"
              @change="toggleSelectAll"
            />
          </template>

          <!-- Loading Spinner -->
          <template #table-busy>
            <div class="text-center text-primary my-5">
              <b-spinner class="align-middle me-2"></b-spinner>
              <strong>Loading data...</strong>
            </div>
          </template>

          <!-- Empty State -->
          <template #empty>
            <div class="text-center py-5">
              <div
                class="empty-state-icon mx-auto mb-3 bg-light text-muted d-flex align-items-center justify-content-center rounded-circle"
                style="width: 70px; height: 70px"
              >
                <i class="mdi mdi-text-box-search-outline" style="font-size: 2rem"></i>
              </div>
              <h5 class="fw-bold text-dark">No records found</h5>
              <p class="text-muted">There are no invalid reasons matching your criteria.</p>
            </div>
          </template>

          <!-- Column: Row Checkbox -->
          <template #cell(selected)="data">
            <b-form-checkbox
              :checked="isSelected(data.item.invalid_reason_id)"
              @change="toggleSelect(data.item.invalid_reason_id)"
            />
          </template>

          <!-- Column: ID -->
          <template #cell(invalid_reason_id)="data">
            <span class="fw-semibold text-secondary">#{{ data.value }}</span>
          </template>

          <!-- Column: Code -->
          <template #cell(code)="data">
            <b-badge variant="light-primary" class="badge-code px-2 py-1 fs-6">
              {{ data.value }}
            </b-badge>
          </template>

          <!-- Column: Name -->
          <template #cell(name)="data">
            <span class="fw-semibold text-dark">{{ data.value }}</span>
          </template>

          <!-- Column: Created At -->
          <template #cell(created_at)="data">
            <small class="text-muted">{{ formatDate(data.value) }}</small>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div class="d-flex align-items-center gap-2 justify-content-end">
              <b-button
                size="sm"
                variant="light"
                class="btn-icon rounded-circle"
                v-b-tooltip.hover
                title="Edit"
                @click="openEditModal(data.item)"
              >
                <i class="mdi mdi-pencil text-primary"></i>
              </b-button>
              <b-button
                size="sm"
                variant="light"
                class="btn-icon rounded-circle"
                v-b-tooltip.hover
                title="Delete"
                @click="openSingleDeleteModal(data.item)"
              >
                <i class="mdi mdi-trash-can-outline text-danger"></i>
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Pagination Bar -->
        <div
          v-if="items.length > 0 && !isLoading"
          class="card-footer bg-white border-top-0 p-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"
        >
          <div class="text-muted pagination-info d-flex align-items-center flex-wrap gap-2">
            <span>Showing</span>
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              size="sm"
              class="w-auto border-0 bg-light rounded-pill px-3"
              @change="applyFiltersWithReset"
            />
            <span>
              entries.
              <strong class="text-dark">{{
                totalRows > 0 ? (currentPage - 1) * perPage + 1 : 0
              }}</strong>
              -
              <strong class="text-dark">{{ Math.min(currentPage * perPage, totalRows) }}</strong>
              of <strong class="text-dark">{{ totalRows }}</strong>
            </span>
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="md"
            class="mb-0 modern-pagination"
            @change="changePage"
          />
        </div>
      </div>
    </div>

    <!-- Form Modal (Add / Edit) -->
    <b-modal
      v-model="showFormModal"
      :title="isEditMode ? 'Edit Invalid Reason' : 'Add New Invalid Reason'"
      centered
      hide-footer
      header-close-content="&times;"
    >
      <b-form @submit.prevent="handleSave">
        <!-- Code Input -->
        <b-form-group label="Code" label-for="input-code" class="mb-3 fw-medium">
          <b-form-input
            id="input-code"
            v-model="formData.code"
            placeholder="e.g. BLUR, WRONG_OBJECT"
            required
            :disabled="isEditMode"
            class="rounded-3"
          />
        </b-form-group>

        <!-- Name Input -->
        <b-form-group label="Reason Name" label-for="input-name" class="mb-4 fw-medium">
          <b-form-input
            id="input-name"
            v-model="formData.name"
            placeholder="e.g. Gambar buram / tidak fokus"
            required
            class="rounded-3"
          />
        </b-form-group>

        <div class="d-flex justify-content-end gap-2 pt-2 border-top">
          <b-button
            variant="light"
            class="rounded-pill px-4"
            @click="showFormModal = false"
            :disabled="isSaving"
          >
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" class="rounded-pill px-4" :disabled="isSaving">
            <b-spinner v-if="isSaving" small class="me-1"></b-spinner>
            <i v-else class="mdi mdi-check me-1"></i>
            {{ isSaving ? 'Saving...' : 'Save' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Delete Confirmation Modal (Single & Bulk) -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      centered
      header-bg-variant="danger"
      header-text-variant="white"
    >
      <div class="text-center py-3">
        <i class="mdi mdi-alert-circle-outline text-danger mb-3" style="font-size: 4rem"></i>
        <h5>Are you sure?</h5>
        <p v-if="deleteMode === 'single'" class="text-muted mb-0">
          You are about to delete reason:
          <strong class="text-dark">{{ selectedItem?.name }}</strong> ({{ selectedItem?.code }}).
        </p>
        <p v-else class="text-muted mb-0">
          You are about to delete <strong>{{ selectedIds.length }}</strong> selected record(s).
        </p>
        <p class="text-muted small mt-1">This action cannot be undone.</p>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="light" @click="cancel" :disabled="isDeleting" class="rounded-pill px-4">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          @click="confirmDelete"
          :disabled="isDeleting"
          class="rounded-pill px-4"
        >
          <b-spinner v-if="isDeleting" small class="me-1"></b-spinner>
          <i v-else class="mdi mdi-trash-can-outline me-1"></i>
          {{ isDeleting ? 'Deleting...' : 'Yes, Delete' }}
        </b-button>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import * as datasetInvalidReasonApi from '@/services/rest-api-sms/lib/dataset-invalid-reason/dataset-invalid-reason'

export default {
  name: 'InvalidReason',
  page: {
    title: 'Invalid Reason',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  data() {
    return {
      title: 'Invalid Reason',
      items: [],
      isLoading: false,
      isSaving: false,
      isDeleting: false,

      // Selection & Bulk Actions
      selectedIds: [],
      deleteMode: 'single', // 'single' | 'bulk'

      // Pagination & Filters
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 20, 50],
      filters: {
        search: null,
      },
      searchDebounceTimer: null,

      // Modals & Form State
      showFormModal: false,
      showDeleteModal: false,
      isEditMode: false,
      selectedItem: null,

      formData: {
        invalid_reason_id: null,
        code: '',
        name: '',
      },

      fields: [
        {
          key: 'selected',
          label: '',
          thClass: 'ps-3 text-center',
          tdClass: 'ps-3 text-center',
          width: '40px',
        },
        {
          key: 'invalid_reason_id',
          label: 'ID',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          width: '80px',
        },
        { key: 'code', label: 'Code', sortable: true, width: '180px' },
        { key: 'name', label: 'Invalid Reason Name', sortable: true },
        { key: 'created_at', label: 'Created At', sortable: true, width: '180px' },
        {
          key: 'actions',
          label: 'Actions',
          thClass: 'text-end pe-4',
          tdClass: 'text-end pe-4',
          width: '120px',
        },
      ],
    }
  },
  computed: {
    isAllSelected() {
      return (
        this.items.length > 0 &&
        this.items.every((item) => this.selectedIds.includes(item.invalid_reason_id))
      )
    },
    isSomeSelected() {
      return this.selectedIds.length > 0 && !this.isAllSelected
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    // ----------------------------------------------------
    // FETCH DATA
    // ----------------------------------------------------
    async loadData() {
      this.isLoading = true
      try {
        const queryParams = {
          page: this.currentPage,
          limit: this.perPage,
          filters: this.filters.search
            ? [`name ilike '%${this.filters.search}%' or code ilike '%${this.filters.search}%'`]
            : undefined,
        }

        const response = await datasetInvalidReasonApi.get(queryParams)
        this.items = response?.data?.paginated_result || response?.data || []
        this.totalRows = response?.metadata?.total_count || this.items.length
        this.selectedIds = [] // Reset pilihan saat pindah halaman/refresh
      } catch (error) {
        if (this.$mcToast) this.$mcToast.error('Failed to load dataset invalid reasons')
      } finally {
        this.isLoading = false
      }
    },

    // ----------------------------------------------------
    // SELECTION LOGIC
    // ----------------------------------------------------
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    toggleSelect(id) {
      const index = this.selectedIds.indexOf(id)
      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(id)
      }
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedIds = []
      } else {
        this.selectedIds = this.items.map((item) => item.invalid_reason_id)
      }
    },

    // ----------------------------------------------------
    // SEARCH & PAGINATION
    // ----------------------------------------------------
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.applyFiltersWithReset()
      }, 400)
    },
    applyFiltersWithReset() {
      this.currentPage = 1
      this.loadData()
    },
    changePage(page) {
      this.currentPage = page
      this.loadData()
    },
    resetFilters() {
      this.filters.search = null
      this.applyFiltersWithReset()
    },

    // ----------------------------------------------------
    // CREATE / UPDATE HANDLERS
    // ----------------------------------------------------
    openCreateModal() {
      this.isEditMode = false
      this.formData = { invalid_reason_id: null, code: '', name: '' }
      this.showFormModal = true
    },
    openEditModal(item) {
      this.isEditMode = true
      this.formData = {
        invalid_reason_id: item.invalid_reason_id,
        code: item.code,
        name: item.name,
      }
      this.showFormModal = true
    },
    async handleSave() {
      this.isSaving = true
      try {
        if (this.isEditMode) {
          await datasetInvalidReasonApi.update(this.formData.invalid_reason_id, {
            name: this.formData.name,
          })
          if (this.$mcToast) this.$mcToast.success('Reason updated successfully')
        } else {
          await datasetInvalidReasonApi.create({
            code: this.formData.code,
            name: this.formData.name,
          })
          if (this.$mcToast) this.$mcToast.success('Reason created successfully')
        }

        this.showFormModal = false
        await this.loadData()
      } catch (error) {
        if (this.$mcToast) this.$mcToast.error('Failed to save reason')
      } finally {
        this.isSaving = false
      }
    },

    // ----------------------------------------------------
    // DELETE HANDLERS (SINGLE & BULK)
    // ----------------------------------------------------
    openSingleDeleteModal(item) {
      this.deleteMode = 'single'
      this.selectedItem = item
      this.showDeleteModal = true
    },
    openBulkDeleteModal() {
      if (this.selectedIds.length === 0) return
      this.deleteMode = 'bulk'
      this.showDeleteModal = true
    },
    async confirmDelete() {
      this.isDeleting = true
      try {
        if (this.deleteMode === 'single' && this.selectedItem) {
          await datasetInvalidReasonApi.deleteById(this.selectedItem.invalid_reason_id)
          if (this.$mcToast) this.$mcToast.success('Reason deleted successfully')
        } else if (this.deleteMode === 'bulk' && this.selectedIds.length > 0) {
          await datasetInvalidReasonApi.deleteBulk(this.selectedIds)
          if (this.$mcToast)
            this.$mcToast.success(`Successfully deleted ${this.selectedIds.length} items`)
        }

        this.showDeleteModal = false
        this.selectedItem = null
        this.selectedIds = []
        await this.loadData()
      } catch (error) {
        if (this.$mcToast) this.$mcToast.error('Failed to delete data')
      } finally {
        this.isDeleting = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}

.rounded-xl {
  border-radius: 12px !important;
}
.form-control-rounded {
  border-radius: 50px !important;
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
}

.search-box .search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  font-size: 1.2rem;
}

.selection-toolbar {
  background-color: #e0f2fe;
  border: 1px solid #bae6fd;
}

.custom-table >>> th {
  background-color: #f8f9fa;
  color: #4b5563;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #edf2f7;
}

.custom-table >>> td {
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid #edf2f7;
}

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background-color: #f1f5f9;
  transform: translateY(-1px);
}

.badge-code {
  background-color: #e0f2fe;
  color: #0369a1;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.pagination-info {
  font-size: 0.9rem;
}
</style>
